import { Button, Modal, notification } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useState, useEffect, useRef, } from 'react';
import { connect } from 'react-redux';
import { CombinedState } from 'reducers';
import { frameToRelativeSeconds, getTimelineFrameRate } from 'utils/media-time';
import { Col, Row } from 'antd/lib/grid';
import { getCore, MLModel, ObjectState } from 'cvat-core-wrapper';
import { modalUpdateAsync, removeObject, saveAnnotationsAsync, updateAnnotationsAsync, updateJobAttrbutes } from 'actions/annotation-actions';
import { updateJobAsync } from 'actions/tasks-actions';
import { useHistory } from 'react-router';
import Phase1aConfirmation from './phase1a-confirmation';
import FaceDuplicateRemoval from './face-selection';
import Phase1bQuestionnaire from './phase1b-questionnaire';
import Phase2aPopupQuestionnaire from './phase2a-popup-questionnaire';
import Phase2bPopupQuestionnaire from './phase2b-popup-questionnaire';
import Phase3aPopupQuestionnaire from './phase3a-popup-questionnaire';

interface Props {
};

const core = getCore();

const COUNT_PHASE2A_POPUPS = 2;


interface StateToProps {
    visible: boolean;
    mode: string;
    states: any[],
    allStates: any[],
    audioData: ArrayBuffer,
    frameSpeed: number,
    dataFrameRate: number | null,
    people: {
        clientID: number;
        frameImage: any
    }[],
    jobInstance: any;
    facematcher: MLModel;
    transcriptData: any;
    jobAttributeSpec: any[],
    jobAttributes: any
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            modal: {
                visible,
                mode,
                people
            },
            annotations: {
                allStates,
                jobAttributes,
            },
            player: {
                audio: {
                    data: audioData
                },
                transcript: {
                    data: transcriptData
                }
            },
            job: {
                instance: jobInstance,
                attributes: {self: jobAttributeSpec}
            }
        },
        settings: {
            player: {
                frameSpeed,
            },
        },
        models: {
            facematchers
        }
    } = state;


    return {
        visible,
        audioData,
        frameSpeed,
        dataFrameRate: jobInstance.dataFrameRate,
        transcriptData,
        mode,
        states: people.map(person => allStates.filter(state => state.clientID === person.clientID)[0]).filter(person => person != null),
        allStates: allStates,
        people: people.filter(person => allStates.some(state => state.clientID === person.clientID)),
        jobInstance,
        facematcher: facematchers[0],
        jobAttributeSpec,
        jobAttributes
    };
}


interface DispatchToProps {
    onUpdateAnnotations(states: ObjectState[]): void;
    onRemoveAnnotation(objectState: any): void;
    updateJob(jobInstance: any): void;
    modalUpdate(update: any): void;
    onSaveAnnotation(sessionInstance: any): void;
    onUpdateJobAttributes(id: number, value: string): void;
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        onUpdateAnnotations(states: ObjectState[]): void {
            dispatch(updateAnnotationsAsync(states));
        },
        onUpdateJobAttributes(id: number, value: string): void {
            dispatch(updateJobAttrbutes(id, value));
        },
        onRemoveAnnotation(objectState: any): void {
            dispatch(removeObject(objectState, false));
        },
        modalUpdate(update: any): void {
            dispatch(modalUpdateAsync(update));
        },
        updateJob(jobInstance: any): void {
            dispatch(updateJobAsync(jobInstance));
        },
        onSaveAnnotation(sessionInstance: any): void {
            dispatch(saveAnnotationsAsync(sessionInstance));
        },
    };
}


function PersonQuestionModal(props: Props & StateToProps & DispatchToProps) {
    const {visible, mode, states, people, audioData, frameSpeed, dataFrameRate, jobInstance, facematcher, allStates, transcriptData,
        jobAttributeSpec, jobAttributes,
        onUpdateAnnotations, onRemoveAnnotation, modalUpdate, updateJob, onSaveAnnotation,
        onUpdateJobAttributes
    } = props;
    const frameRate = getTimelineFrameRate(dataFrameRate);
    const [croppedImages, setCroppedImages] = useState<any[]>([]);
    const [images, setImages] = useState<HTMLImageElement[]>([]);
    const audio = useRef<HTMLAudioElement[]>([]);
    const audiolisteners = useRef<((e: any) => void)[]>([]);

    const history = useHistory();

    const showPullNextModal = () => {
        Modal.confirm({
            title: 'Submit Successful',
            content: 'Would you like to claim and proceed directly to your next assigned task?',
            okText: 'Yes, Claim Next',
            cancelText: 'No, Go to Dashboard',
            onOk: () => {
                const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];
                const queryParams = new URLSearchParams(window.location.search);
                const org = queryParams.get('org');
                const url = org ? `/api/jobs/claim-next?org=${org}` : '/api/jobs/claim-next';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken || '',
                    },
                })
                .then(res => res.json())
                .then(data => {
                    if (data.job_id && data.task_id) {
                        notification.success({ message: 'Successfully claimed next task.' });
                        window.location.href = `/tasks/${data.task_id}/jobs/${data.job_id}${org ? `?org=${org}` : ''}`;
                    } else {
                        notification.warning({ message: data.detail || 'No eligible tasks are available.' });
                        history.push(org ? `/jobs?org=${org}` : '/jobs');
                    }
                })
                .catch(() => {
                    notification.error({ message: 'Error claiming next job auto-assignment.' });
                    history.push(org ? `/jobs?org=${org}` : '/jobs');
                });
            },
            onCancel: () => {
                const queryParams = new URLSearchParams(window.location.search);
                const org = queryParams.get('org');
                history.push(org ? `/jobs?org=${org}` : '/jobs');
            }
        });
    };

    const [audioBlobURL, setAudioBlobURL] = useState<string>('');
    const [audioPlayingIdx, setAudioPlayingIdx] = useState(-1);

    const [closeButtonDisabled, setDoneButtonDisabled] = useState(true);


    useEffect(() => {
        if (!visible) {
            images.forEach((image) => {
                if (image.src) {
                    URL.revokeObjectURL(image.src);
                }
            });
            setImages([]);

            audio.current.forEach((rf, idx) => {
                rf.pause();
                rf.removeEventListener('timeupdate', audiolisteners.current[idx])
            });
            setAudioPlayingIdx(-1);
        } else {
            if (mode === 'similar_face' && states.length === 1) {
                if (facematcher) {
                    (async function(){
                        const targetPerson = states[0];
                        const sameLabelVisiblePeople = allStates.filter(
                            state => state.label.id === targetPerson.label.id &&
                            state.objectType === 'shape' &&
                            state.clientID !== targetPerson.clientID
                        );

                        const tp1X = Math.floor(Math.min(targetPerson.points[0], targetPerson.points[2]));
                        const tp2X = Math.floor(Math.max(targetPerson.points[0], targetPerson.points[2]));

                        const tp1Y = Math.floor(Math.min(targetPerson.points[1], targetPerson.points[3]));
                        const tp2Y = Math.floor(Math.max(targetPerson.points[1], targetPerson.points[3]));
                        const data = {
                            frames: sameLabelVisiblePeople.map(state => state.frame),
                            target_frame: targetPerson.frame,
                            boxes: sameLabelVisiblePeople.map(state => {
                                const p1X = Math.floor(Math.min(state.points[0], state.points[2]));
                                const p2X = Math.floor(Math.max(state.points[0], state.points[2]));

                                const p1Y = Math.floor(Math.min(state.points[1], state.points[3]));
                                const p2Y = Math.floor(Math.max(state.points[1], state.points[3]));

                                return [p1X, p1Y, p2X, p2Y];
                            }),
                            targetbox: [tp1X, tp1Y, tp2X, tp2Y]
                        }

                        try {
                            const response = await core.lambda.call(jobInstance.taskId, facematcher,
                                { ...data, job: jobInstance.id, frame: targetPerson.frame });

                            if (response.verified) {
                                modalUpdate({
                                    mode: 'similar_face',
                                    people: [{
                                        clientID: targetPerson.clientID,
                                        frameNumber: targetPerson.frame
                                    },
                                    {
                                        clientID: sameLabelVisiblePeople[response.idx].clientID,
                                        frameNumber: sameLabelVisiblePeople[response.idx].frame
                                    }
                                ],
                                    visible: true
                                })
                            } else {
                                modalUpdate({
                                    // mode: 'phase1b_questionnaire',
                                    // people: [{
                                    //     clientID: targetPerson.clientID,
                                    //     frameNumber: targetPerson.frame
                                    // }],
                                    visible: false,
                                    people: []
                                })
                            }
                        } catch(error) {
                            modalUpdate({
                                visible: false,
                                people: []
                            })
                        }
                    })();
                } else {
                    modalUpdate({
                        visible: false,
                        people: []
                    })
                }

            } else {
                const images = states.map((_, idx) => {
                    const img = new Image();
                    return img;
                });
                setImages(images);
            }
        }

    }, [visible, mode, states.length]);


    useEffect(() => {
        if (!images.length) {
            setCroppedImages([]);
        } else {
            Promise.all(images.map(async (image, idx) => {
                const cnv = document.createElement('canvas');
                const ctx = cnv.getContext('2d');

                const p1X = Math.min(states[idx].points[0], states[idx].points[2]);
                const p2X = Math.max(states[idx].points[0], states[idx].points[2]);

                const p1Y = Math.min(states[idx].points[1], states[idx].points[3]);
                const p2Y = Math.max(states[idx].points[1], states[idx].points[3]);

                const sX = p1X,
                    sY = p1Y,
                    sW = p2X - p1X,
                    sH = p2Y - p1Y;

                cnv.width = sW;
                cnv.height = sH;
                return await (new Promise((resolve) => {
                    image.onload = () => {
                        ctx?.drawImage(image, sX, sY, sW, sH, 0, 0, sW, sH);
                        const cropped = cnv.toDataURL();
                        cnv.remove();
                        resolve(cropped);
                    };
                    image.src = URL.createObjectURL(people[idx].frameImage);
                }));
            })).then(imgs => setCroppedImages(imgs));

        }
    }, [images]);

    const onOK = () => modalUpdate({
        visible: false,
        people: [],
    });

    const renderDoneButton = (onClick: () => void, disabled = closeButtonDisabled): JSX.Element => (
        <Button
            type='primary'
            size='large'
            className='cvat-person-modal-done-button'
            disabled={disabled}
            onClick={onClick}
        >
            Done
        </Button>
    );

    // clean-up
    useEffect(() => () => {
        images.forEach((image) => {
            if (image.src) {
                URL.revokeObjectURL(image.src);
            }
        });

        if (audioBlobURL) {
            URL.revokeObjectURL(audioBlobURL);
        }

        audio.current.forEach((rf, idx) => {
            rf.pause();
            rf.removeEventListener('timeupdate', audiolisteners.current[idx])
        });
        audio.current = [];

        setAudioPlayingIdx(-1);
    }, []);

    useEffect(() => {
        if(audioData) {
            const blb = new Blob([audioData], { type: 'audio/mp4' });
            setAudioBlobURL(URL.createObjectURL(blb));
        }
    }, [audioData])

    const playAudio = (idx: number) => {
        audio.current.forEach((rf, i) => {
            if (audioPlayingIdx === -1) {
                if (i === idx ) {
                    rf.currentTime = frameToRelativeSeconds(states[idx].frame, jobInstance.startFrame, frameRate);
                    rf.play();
                    setAudioPlayingIdx(i);
                }
            }
            else {
                rf.pause();
                setAudioPlayingIdx(-1);
            }
        });
    }



    return (
      <Modal
      className='cvat-person-question-modal'
      visible={visible}
      width={'80%'}
      style={{ top: 24 }}
      bodyStyle={{ maxHeight: '72vh', overflowY: 'auto', paddingBottom: 24 }}
      onOk={onOK} footer={
        (mode === 'similar_face' && states.length !== 2 && []) ||
        (mode === 'similar_face' && states.length === 2 && [
            <Button key="yes" type="primary" onClick={
                () => {
                    onRemoveAnnotation(states[0]);
                    modalUpdate({
                        visible: false,
                        people: [],
                    });
                }
            }>Yes, they are the same people</Button>,

            <Button key="no" onClick={
                () => {
                    modalUpdate({
                        visible: false,
                        people: [],
                    })
                }
            }>No, they are different people</Button>
        ]) ||
        (mode === 'phase1b_before_submit' &&
            renderDoneButton(() => {
                onSaveAnnotation(jobInstance);
                onOK();
                setTimeout(() => {
                    jobInstance.phase = jobInstance.phase_version === '2' ? 'phase2a' : 'phase3a';
                    jobInstance.state = core.enums.JobState.NEW;
                    updateJob(jobInstance);
                    setTimeout(() => {
                        showPullNextModal();
                    }, 1000);
                }, 1000);
            }, closeButtonDisabled || states
                .some(state => state.label.attributes.filter((attr: any) => attr.name.startsWith('demographics.') && !attr.nullable).
                some((attr: any) => state.attributes[attr.id] === '')))
        ) ||
        (mode === 'phase1b_questionnaire' &&
            renderDoneButton(onOK)
        ) ||
        (mode === 'duplicate_face_removal' &&
            renderDoneButton(() => {
                modalUpdate({
                    mode: 'phase1a_assignment_confirmation',
                    visible: true,
                    people: states.map(state => ({frameNumber: state.frame, clientID: state.clientID})),
                });
            })
        ) ||
        (mode === 'phase1a_assignment_confirmation' &&
            renderDoneButton(() => {
                onSaveAnnotation(jobInstance);
                onOK();
                setTimeout(() => {
                    jobInstance.state = core.enums.JobState.NEW;
                    jobInstance.phase = 'phase1b';
                    updateJob(jobInstance);
                    window.location.reload();
                }, 1000);
            })
        ) ||
        (mode != null && mode.startsWith('phase2a-auto-popup_') &&
            renderDoneButton(() => {
                onOK();
            })
        ) ||
        (mode != null && mode.startsWith('phase2b-auto-popup_') &&
            renderDoneButton(() => {
            onOK();
        })
        ) ||
        (mode != null && mode.startsWith('phase3a-auto-popup_') &&
            renderDoneButton(() => {
                onOK();
            })
        )

      } onCancel={() => {
            modalUpdate({
                visible: false,
                people: [],
            })
      }} >
            {
                mode === 'phase1b_questionnaire' && (
                    <Phase1bQuestionnaire beforeSubmit={false}
                    audio={audio} audioBlobURL={audioBlobURL} audioPlayingIdx={audioPlayingIdx}
                    audiolisteners={audiolisteners} croppedImages={croppedImages}
                    frameSpeed={frameSpeed} jobAttributeSpec={jobAttributeSpec} jobAttributes={jobAttributes}
                    onUpdateAnnotations={onUpdateAnnotations} onUpdateJobAttributes={onUpdateJobAttributes}
                    playAudio={playAudio} setAudioPlayingIdx={setAudioPlayingIdx} setDoneButtonDisabled={setDoneButtonDisabled}
                    states={[states[0]]}
                    />
            )}

            {
                mode === 'phase1b_before_submit' && (
                    <Phase1bQuestionnaire beforeSubmit={true}
                    audio={audio} audioBlobURL={audioBlobURL} audioPlayingIdx={audioPlayingIdx}
                    audiolisteners={audiolisteners} croppedImages={croppedImages}
                    frameSpeed={frameSpeed} jobAttributeSpec={jobAttributeSpec} jobAttributes={jobAttributes}
                    onUpdateAnnotations={onUpdateAnnotations} onUpdateJobAttributes={onUpdateJobAttributes}
                    playAudio={playAudio} setAudioPlayingIdx={setAudioPlayingIdx} setDoneButtonDisabled={setDoneButtonDisabled}
                    states={states}
                    />
                )
            }
            {
                mode === 'similar_face' && (
                    states.length !== 2 ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Text strong style={{ fontSize: '18px' }}>Searching for similar faces...</Text>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', paddingTop: '10px' }}>
                            <div style={{ marginBottom: '24px' }}>
                                <Text strong style={{ fontSize: '18px' }}>We think this is a similar face</Text>
                                <br />
                                <Text type="secondary">Does the person on the left match the person on the right?</Text>
                            </div>
                            <Row gutter={24} justify="center" align="middle">
                                <Col span={10} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Text strong style={{ marginBottom: '8px' }}>Target Person</Text>
                                    {croppedImages[0] && (
                                        <div style={{
                                            width: '100%',
                                            height: '300px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: '#f0f2f5',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }}>
                                            <img style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} src={croppedImages[0]} alt="Target Person" />
                                        </div>
                                    )}
                                </Col>
                                <Col span={4} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#bfbfbf' }}>VS</div>
                                </Col>
                                <Col span={10} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Text strong style={{ marginBottom: '8px' }}>Similar Face</Text>
                                    {croppedImages[1] && (
                                        <div style={{
                                            width: '100%',
                                            height: '300px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: '#f0f2f5',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }}>
                                            <img style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} src={croppedImages[1]} alt="Similar Face" />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    )
                )
            }
            {
                mode === 'duplicate_face_removal' && states.length && (
                    <FaceDuplicateRemoval audio={audio} audioBlobURL={audioBlobURL}
                        audioPlayingIdx={audioPlayingIdx} audiolisteners={audiolisteners}
                        croppedImages={croppedImages} frameSpeed={frameSpeed}
                        onRemoveAnnotation={onRemoveAnnotation} playAudio={playAudio}
                        setAudioPlayingIdx={setAudioPlayingIdx} setDoneButtonDisabled={setDoneButtonDisabled}
                        states={states}
                    />
                )
            }
            {
                mode === 'phase1a_assignment_confirmation' && states.length && (
                    <Phase1aConfirmation
                        audio={audio} audioBlobURL={audioBlobURL} audioPlayingIdx={audioPlayingIdx}
                        croppedImages={croppedImages} frameSpeed={frameSpeed} states={states}
                        audiolisteners={audiolisteners} playAudio={playAudio}  setAudioPlayingIdx={setAudioPlayingIdx}
                        transcriptData={transcriptData}
                        setDoneButtonDisabled={setDoneButtonDisabled}
                    />
                )
            }
            {
                mode != null && mode.startsWith('phase2a-auto-popup_') && states.length && (
                    <Phase2aPopupQuestionnaire
                        audio={audio} audioBlobURL={audioBlobURL} audioPlayingIdx={audioPlayingIdx}
                        audiolisteners={audiolisteners}
                        croppedImages={croppedImages}
                        frameSpeed={frameSpeed}
                        jobAttributeSpec={jobAttributeSpec}
                        jobAttributes={jobAttributes}
                        onUpdateAnnotations={onUpdateAnnotations}
                        onUpdateJobAttributes={onUpdateJobAttributes}
                        playAudio={playAudio}
                        setAudioPlayingIdx={setAudioPlayingIdx}
                        setDoneButtonDisabled={setDoneButtonDisabled}
                        states={states}
                        popupNumber={parseInt(mode.split('_')[1])}
                        jobInstance={jobInstance}
                    />
                )
            }
            {
                mode != null && mode.startsWith('phase2b-auto-popup_') && states.length && (
                    <Phase2bPopupQuestionnaire
                        audio={audio} audioBlobURL={audioBlobURL} audioPlayingIdx={audioPlayingIdx}
                        audiolisteners={audiolisteners}
                        croppedImages={croppedImages}
                        frameSpeed={frameSpeed}
                        jobAttributeSpec={jobAttributeSpec}
                        jobAttributes={jobAttributes}
                        onUpdateAnnotations={onUpdateAnnotations}
                        onUpdateJobAttributes={onUpdateJobAttributes}
                        playAudio={playAudio}
                        setAudioPlayingIdx={setAudioPlayingIdx}
                        setDoneButtonDisabled={setDoneButtonDisabled}
                        states={states}
                        isRecurring={mode.includes('recurring')}
                        popupFrame={parseInt(mode.split('_').slice(-1)[0])}
                        popupNumber={parseInt(mode.split('_').slice(-1)[0])}
                        isLastRecurringPopup={mode.includes('phase2b-auto-popup') && !(mode.includes('recurring'))}
                        jobInstance={jobInstance}

                    />
                )
            }
            {
                mode != null && mode.startsWith('phase3a-auto-popup_') && states.length && (
                    <Phase3aPopupQuestionnaire
                        audio={audio} audioBlobURL={audioBlobURL} audioPlayingIdx={audioPlayingIdx}
                        audiolisteners={audiolisteners}
                        croppedImages={croppedImages}
                        frameSpeed={frameSpeed}
                        jobAttributeSpec={jobAttributeSpec}
                        jobAttributes={jobAttributes}
                        onUpdateAnnotations={onUpdateAnnotations}
                        onUpdateJobAttributes={onUpdateJobAttributes}
                        playAudio={playAudio}
                        setAudioPlayingIdx={setAudioPlayingIdx}
                        setDoneButtonDisabled={setDoneButtonDisabled}
                        states={states}
                        popupNumber={parseInt(mode.split('_')[1])}
                        jobInstance={jobInstance}
                    />
                )
            }
      </Modal>

    )
}

export default connect(mapStateToProps, mapDispatchToProps)(PersonQuestionModal);
